import { afterNextRender, ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatColumnDef, MatTable, MatTableModule } from '@angular/material/table';
import {
	ARA_GROUP_ADD,
	ARA_GROUP_HEADER,
	ARA_GROUP_MORE,
	GroupedRow,
	TableGroup,
	TableGroupAddRow,
	TableGroupHeaderRow,
	TableGroupMoreRow,
} from '../table-grouping';

/** Kontext des projizierten Kopfzeilen-Templates (`let-group="group"`). */
export interface AraGroupHeaderContext<T> {
	$implicit: TableGroup<T>;
	group: TableGroup<T>;
}

/** Kontext des projizierten „Alle anzeigen"-Templates. */
export interface AraGroupMoreContext<T> {
	$implicit: TableGroupMoreRow<T>['__araGroupMore'];
	group: TableGroup<T>;
	hiddenCount: number;
	expanded: boolean;
}

/** Kontext des projizierten Add-Zeilen-Templates (`let-group="group"`). */
export interface AraGroupAddContext<T> {
	$implicit: TableGroup<T>;
	group: TableGroup<T>;
}

/**
 * Geteilte Gruppierungs-Marker-SPALTEN (Kopfzeile · „Alle anzeigen" · Add-Zeile) für eine `mat-table`.
 *
 * `mat-table` kennt keine Zeile über die volle Breite und keine heterogene Datasource; eine Zeilenart
 * wird ausschließlich über einen `matColumnDef`-Namen ausgedrückt (der dokumentierte Material-Workaround).
 * Diese Komponente kapselt die drei Marker-Spalten (`__araGroup*`) mit `colspan` und den geteilten
 * `.ara-table-group-*`-Klassen an EINER Stelle, statt sie in jedem Consumer von Hand zu wiederholen; den
 * Zellinhalt projiziert der Consumer typisiert (keine Casts mehr im Consumer).
 *
 * Verwendung: als Element-Kind INNERHALB der `<table mat-table>` platzieren (über den Element-Injector
 * löst {@link MatTable} auf die umschließende Tabelle auf, wie `MatTextColumn`). Die drei zugehörigen
 * `*matRowDef`s deklariert der Consumer selbst über {@link ARA_GROUP_ROW_DEFS} — sie MÜSSEN in der
 * Consumer-View liegen (Timing, siehe Template-Kommentar). Datasource + Collapse-Zustand bleiben wie
 * gehabt beim Consumer (`flattenGroups(...)`).
 *
 * ```html
 * <table mat-table [dataSource]="flatRows()">
 *   <ara-table-group-columns [columnCount]="displayedColumns().length"
 *                            [headerCell]="grpHeader" [moreCell]="grpMore" [addCell]="grpAdd" />
 *   <ng-template #grpHeader let-group="group"> … {{ group.label }} … </ng-template>
 *   <!-- fachliche Spalten + Header-/Daten-Row-Def wie gehabt -->
 *   <tr *matRowDef="let r; columns: groupRowDefs.header.columns; when: groupRowDefs.header.when"
 *       mat-row class="ara-table-group-row"></tr>
 *   <!-- … more/add analog … -->
 * </table>
 * ```
 */
@Component({
	selector: 'ara-table-group-columns',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [NgTemplateOutlet, MatTableModule],
	templateUrl: './ara-table-group-columns.component.html',
	// Der Host sitzt als Kind der <table> und rendert selbst nichts — Marker-Zellen stampft die Tabelle
	// in ihre eigenen Zeilen-Outlets. `hidden` (display:none) verhindert eine leere anonyme Tabellenzeile.
	host: { class: 'hidden' },
})
export class AraTableGroupColumnsComponent<T> implements OnInit, OnDestroy {
	/** Löst auf die UMSCHLIESSENDE Consumer-Tabelle auf (Element-Injector, kein self/skipSelf). */
	private readonly table = inject(MatTable<GroupedRow<T>>);

	/** colspan jeder Marker-Zelle — an `displayedColumns().length` des Consumers binden (reaktiv). */
	readonly columnCount = input.required<number>();
	/** Inhalt der Gruppen-Kopfzeile; Kontext {@link AraGroupHeaderContext}. */
	readonly headerCell = input.required<TemplateRef<AraGroupHeaderContext<T>>>();
	/** Inhalt der „Alle anzeigen"-Zeile; Kontext {@link AraGroupMoreContext}. */
	readonly moreCell = input.required<TemplateRef<AraGroupMoreContext<T>>>();
	/** Inhalt der Add-Zeile; Kontext {@link AraGroupAddContext}. Ohne Add-Zeilen bleibt sie ungenutzt. */
	readonly addCell = input<TemplateRef<AraGroupAddContext<T>>>();

	// Statische Queries → in ngOnInit verfügbar, VOR dem ersten `ngAfterContentChecked` der Tabelle.
	@ViewChild('headerColDef', { read: MatColumnDef, static: true }) private readonly headerColDef!: MatColumnDef;
	@ViewChild('moreColDef', { read: MatColumnDef, static: true }) private readonly moreColDef!: MatColumnDef;
	@ViewChild('addColDef', { read: MatColumnDef, static: true }) private readonly addColDef!: MatColumnDef;

	private get columnDefs(): MatColumnDef[] {
		return [this.headerColDef, this.moreColDef, this.addColDef];
	}

	constructor() {
		// Die Tabelle rendert ihre Daten beim dataSource-Connect — das passiert, bevor unsere in ngOnInit
		// nachgereichten Marker-Spalten registriert sind, sodass die Marker-Zeilen im Erst-Render leer
		// bleiben und der Daten-Differ danach „unverändert" meldet (`renderRows()` ist differ-gated, also
		// wirkungslos). Nach dem ersten Render EINMAL ein vollständiges Neu-Rendern erzwingen.
		afterNextRender(() => this.forceReRender());
	}

	ngOnInit(): void {
		// Spalten-`name` imperativ setzen, BEVOR die Tabelle das erste Mal `_cacheColumnDefs()` läuft:
		// die Bindung `[matColumnDef]` greift in dieser Kind-View evtl. erst nach dem ersten Content-Check
		// (sonst „Duplicate column definition: undefined"). Danach registrieren (Muster: MatTextColumn).
		this.headerColDef.name = ARA_GROUP_HEADER;
		this.moreColDef.name = ARA_GROUP_MORE;
		this.addColDef.name = ARA_GROUP_ADD;
		this.columnDefs.forEach((def) => this.table.addColumnDef(def));
	}

	ngOnDestroy(): void {
		this.columnDefs.forEach((def) => this.table.removeColumnDef(def));
	}

	/**
	 * Vollständiges Neu-Rendern der Datenzeilen (Differ-Reset). Es gibt dafür keine öffentliche API —
	 * `renderRows()` ist differ-gated und würde den Erst-Render (vor der Spalten-Registrierung) nicht
	 * korrigieren; die CDK ruft `_forceRenderDataRows()` intern selbst bei Spalten-/Row-Def-Änderungen.
	 * Zugriff bewusst defensiv (Guard), damit ein CDK-Umbau höchstens die Nach-Registrierung überspringt.
	 *
	 * Regressions-Guard: `ara-slim-table.grouping.spec.ts` rendert die Komponente in einer echten
	 * TestBed-Tabelle und prüft die Marker-DOM-Ausgabe — entfällt oder ändert die CDK diese interne
	 * Methode, schlägt genau dieser Spec fehl (Frühwarnung statt stiller Laufzeit-Regression).
	 */
	private forceReRender(): void {
		const table = this.table as unknown as { _forceRenderDataRows?: () => void; _changeDetectorRef?: { detectChanges: () => void } };
		table._forceRenderDataRows?.();
		// Die neu gestampften Marker-Zellen (ngTemplateOutlet des projizierten Inhalts) im selben Zug
		// change-detecten, sonst erschiene der Zellinhalt erst beim nächsten CD.
		table._changeDetectorRef?.detectChanges();
	}

	/** Verengt die Union auf die Kopfzeilen-Gruppe (gesichert durch `when: isTableGroupHeader`). */
	protected headerContext(row: GroupedRow<T>): AraGroupHeaderContext<T> {
		const group = (row as TableGroupHeaderRow<T>).__araGroupHeader;
		return { $implicit: group, group };
	}

	/** Verengt die Union auf die „Alle anzeigen"-Nutzlast (gesichert durch `when: isTableGroupMoreRow`). */
	protected moreContext(row: GroupedRow<T>): AraGroupMoreContext<T> {
		const more = (row as TableGroupMoreRow<T>).__araGroupMore;
		return { $implicit: more, group: more.group, hiddenCount: more.hiddenCount, expanded: more.expanded };
	}

	/** Verengt die Union auf die Add-Gruppe (gesichert durch `when: isTableGroupAddRow`). */
	protected addContext(row: GroupedRow<T>): AraGroupAddContext<T> {
		const group = (row as TableGroupAddRow<T>).__araGroupAdd;
		return { $implicit: group, group };
	}
}
